import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";
import apiClient from "../api/ResturantClient";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

// const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
// const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
// console.log("Stripe Key:", stripeKey);
const stripePromise = (async () => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key || !key.startsWith("pk_")) {
    console.error("Invalid Stripe key format");
    return null;
  }
  return loadStripe(key);
})();

// Payment Form Component
const PaymentForm = ({ plan, user_id, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment method
      const { error: stripeError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement),
        });

      if (stripeError) {
        setError(stripeError.message);
        return;
      }

      // Create subscription with the payment method
      const response = await axios.post(
        `${config.API_BASE_URL}/subscription/create-subscription`,
        {
          user_id: user_id,
          plan_id: plan.plan_id,
          payment_method_id: paymentMethod.id,
        }
      );

      if (response.data.success) {
        onSuccess();
      }
    } catch (err) {
      setError(
        err.response?.data?.error || "Payment failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-gray-300 rounded-md">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                "::placeholder": {
                  color: "#aab7c4",
                },
              },
              invalid: {
                color: "#9e2146",
              },
            },
          }}
        />
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className={`py-2 px-4 bg-[#4e7ab4] text-white rounded-lg transition-colors ${
            loading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#3a5a8c]"
          }`}
        >
          {loading ? "Processing..." : `Pay ${plan.price}`}
        </button>
      </div>
    </form>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText,
  cancelText,
  isDestructive = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4 text-gray-800">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            {cancelText || "Cancel"}
          </button>
          <button
            onClick={onConfirm}
            className={`py-2 px-4 rounded-lg transition-colors ${
              isDestructive
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-[#4e7ab4] text-white hover:bg-[#3a5a8c]"
            }`}
          >
            {confirmText || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Cancellation Notice Component
const CancellationNotice = ({ subscription, formatDate }) => {
  if (!subscription.cancel_at_period_end) return null;

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-yellow-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-yellow-800">
            <span className="font-medium">Scheduled for Cancellation:</span>{" "}
            Your subscription is set to end on{" "}
            {formatDate(subscription.next_billing_date)}. You will have access
            until this date.
          </p>
        </div>
      </div>
    </div>
  );
};

const SubscriptionDetails = () => {
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const navigate = useNavigate();
  const user_id = parseInt(sessionStorage.getItem("user_id"));

  // Process subscription data to calculate missing fields
  const processSubscriptionData = (data) => {
    if (!data || data.status === "none") {
      return data;
    }

    const processedData = { ...data };
    const today = new Date();

    // If we have start_date and plan_duration_days but no end_date, calculate it
    if (
      processedData.start_date &&
      processedData.plan_duration_days &&
      !processedData.end_date
    ) {
      const startDate = new Date(processedData.start_date);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + processedData.plan_duration_days);
      processedData.end_date = endDate.toISOString();

      // Also calculate next_billing_date if missing (typically 1 day after end_date)
      if (!processedData.next_billing_date) {
        const nextBillingDate = new Date(endDate);
        nextBillingDate.setDate(endDate.getDate() + 1);
        processedData.next_billing_date = nextBillingDate.toISOString();
      }
    }

    // Calculate plan_duration_days from dates if not provided
    if (
      !processedData.plan_duration_days &&
      processedData.start_date &&
      processedData.end_date
    ) {
      const startDate = new Date(processedData.start_date);
      const endDate = new Date(processedData.end_date);
      const diffTime = endDate - startDate;
      processedData.plan_duration_days = Math.ceil(
        diffTime / (1000 * 60 * 60 * 24)
      );
    }

    // Calculate remaining days based on end_date and today
    if (processedData.end_date) {
      const endDate = new Date(processedData.end_date);
      const diffTime = endDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      processedData.remaining_days = Math.max(0, diffDays);
    }
    // If no end_date but we have start_date and plan_duration_days, calculate remaining days
    else if (processedData.start_date && processedData.plan_duration_days) {
      const startDate = new Date(processedData.start_date);
      const totalDurationMs =
        processedData.plan_duration_days * 24 * 60 * 60 * 1000;
      const elapsedMs = today - startDate;
      const remainingMs = totalDurationMs - elapsedMs;
      const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
      processedData.remaining_days = Math.max(0, remainingDays);
    }

    // For canceled subscriptions, remaining days should be 0
    if (
      processedData.status === "canceled" ||
      processedData.status === "expired"
    ) {
      processedData.remaining_days = 0;
    }

    // For subscriptions scheduled for cancellation, set flag
    if (
      processedData.cancel_at_period_end &&
      processedData.status === "active"
    ) {
      processedData.scheduled_for_cancellation = true;
    }

    return processedData;
  };

  // Check if cancellation is allowed (after 5 days from start date)
  const isCancellationAllowed = () => {
    if (!subscription || !subscription.start_date) return false;

    const startDate = new Date(subscription.start_date);
    const today = new Date();
    const daysSinceStart = Math.floor(
      (today - startDate) / (1000 * 60 * 60 * 24)
    );

    return daysSinceStart >= 5;
  };

  // Get days until cancellation is allowed
  const getDaysUntilCancellationAllowed = () => {
    if (!subscription || !subscription.start_date) return 0;

    const startDate = new Date(subscription.start_date);
    const today = new Date();
    const daysSinceStart = Math.floor(
      (today - startDate) / (1000 * 60 * 60 * 24)
    );

    return Math.max(0, 5 - daysSinceStart);
  };

  // Get cancellation date
  const getCancellationDate = () => {
    if (!subscription || !subscription.start_date) return null;

    const startDate = new Date(subscription.start_date);
    const cancellationDate = new Date(startDate);
    cancellationDate.setDate(startDate.getDate() + 5);

    return cancellationDate;
  };

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        setLoading(true);
        // Fetch subscription status
        const subResponse = await apiClient.get(
          `/subscription/subscription-status/${user_id}`
        );

        // Fetch available plans
        const plansResponse = await axios.get(
          `${config.API_BASE_URL}/subscription/plans`
        );

        // Process subscription data to fill in missing values
        const subData = subResponse.data;
        const processedData = processSubscriptionData(subData);

        setSubscription(processedData);
        setPlans(plansResponse.data);
      } catch (err) {
        console.error("Error fetching subscription details:", err);
        setError(
          "Failed to load subscription details. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [user_id]);

  const handleRenewClick = (plan) => {
    setSelectedPlan(plan);
    setShowRenewModal(true);
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      setShowCancelConfirmation(false);

      await apiClient.post(`/subscription/cancel-subscription/${user_id}`);

      // Refresh subscription data
      const response = await apiClient.get(
        `/subscription/subscription-status/${user_id}`
      );
      const processedData = processSubscriptionData(response.data);
      setSubscription(processedData);
    } catch (err) {
      console.error("Error canceling subscription:", err);
      setError("Failed to cancel subscription. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setShowRenewModal(false);
    setLoading(true);

    try {
      // Refresh subscription data
      const response = await apiClient.get(
        `/subscription/subscription-status/${user_id}`
      );
      const processedData = processSubscriptionData(response.data);
      setSubscription(processedData);
    } catch (err) {
      console.error("Error refreshing subscription data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Add this function to your component (can be placed with other utility functions)
  const getGracePeriodDaysInfo = (subscription) => {
    if (
      !subscription ||
      !subscription.end_date ||
      !subscription.grace_period_end_date ||
      !subscription.is_in_grace_period
    ) {
      return null;
    }

    const now = new Date();
    const endDate = new Date(subscription.end_date);
    const gracePeriodEndDate = new Date(subscription.grace_period_end_date);

    // Ensure dates are valid
    if (isNaN(endDate.getTime()) || isNaN(gracePeriodEndDate.getTime())) {
      return null;
    }

    // Calculate total grace period days (from next_billing_date to grace_period_end_date)
    const totalGraceDays = 7; // Fixed to 7 days as per requirement

    // Calculate days passed since grace period started (end_date)
    const daysPassed = Math.floor((now - endDate) / (1000 * 60 * 60 * 24));

    // Calculate current day in grace period (1 to 7)
    const currentGraceDay = Math.min(
      Math.max(1, daysPassed + 1),
      totalGraceDays
    );

    // Calculate remaining days in grace period
    const remainingMs = gracePeriodEndDate - now;
    const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));

    return {
      currentDay: currentGraceDay,
      remainingDays: Math.max(0, remainingDays),
      totalGraceDays: totalGraceDays,
    };
  };

  // Add with your other utility functions
  const isEndDateWithin5Days = (subscription) => {
    if (
      !subscription ||
      !subscription.next_billing_date ||
      subscription.status !== "active"
    ) {
      return false;
    }

    const nextBillingDate = new Date(subscription.next_billing_date);
    const today = new Date();
    const timeDiff = nextBillingDate - today;
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return daysDiff <= 5 && daysDiff >= 0;
  };

  const getDaysUntilEndDate = (subscription) => {
    if (!subscription || !subscription.next_billing_date) return 0;

    const nextBillingDate = new Date(subscription.next_billing_date);
    const today = new Date();
    const timeDiff = nextBillingDate - today;
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  };

  const getStatusColor = (status, isScheduledForCancellation = false) => {
    if (isScheduledForCancellation) {
      return "text-orange-600";
    }

    switch (status) {
      case "active":
        return "text-green-600";
      case "canceled":
        return "text-yellow-600";
      case "expired":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Subscription Details
          </h2>
          <div className="bg-red-100 p-4 rounded-lg text-red-700">{error}</div>
          <button
            onClick={() => navigate(-1)}
            className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Back to Profile
          </button>
        </div>
      </div>
    );
  }

  const cancellationAllowed = isCancellationAllowed();
  const daysUntilCancellation = getDaysUntilCancellationAllowed();
  const cancellationDate = getCancellationDate();

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Subscription Details
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Back to Profile
          </button>
        </div>

        {/* Scheduled Cancellation Notice */}
        {subscription && subscription.cancel_at_period_end && (
          <CancellationNotice
            subscription={subscription}
            formatDate={formatDate}
          />
        )}

        {/* 5-Day Warning Message */}
        {subscription &&
          isEndDateWithin5Days(subscription) &&
          subscription.status === "active" &&
          !subscription.cancel_at_period_end && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-orange-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-orange-800">
                    <span className="font-medium">
                      Subscription Ending Soon:
                    </span>{" "}
                    Your subscription will expire in{" "}
                    {getDaysUntilEndDate(subscription)} day
                    {getDaysUntilEndDate(subscription) !== 1 ? "s" : ""} on{" "}
                    {formatDate(subscription.end_date)}.
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Grace Period Message */}
        {subscription && subscription.status === "grace_period" && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-800">
                  <span className="font-medium">Grace Period:</span>{" "}
                  {getGracePeriodDaysInfo(subscription) ? (
                    <>
                      You're on day{" "}
                      {getGracePeriodDaysInfo(subscription).currentDay} of{" "}
                      {getGracePeriodDaysInfo(subscription).totalGraceDays}.
                      Your access will expire in{" "}
                      {getGracePeriodDaysInfo(subscription).remainingDays} day
                      {getGracePeriodDaysInfo(subscription).remainingDays !== 1
                        ? "s"
                        : ""}
                      .
                    </>
                  ) : (
                    "Your subscription is in grace period. Please renew to continue access."
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {subscription && subscription.status !== "none" ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Status
                </h3>
                <p
                  className={`font-semibold ${getStatusColor(
                    subscription.status,
                    subscription.cancel_at_period_end
                  )}`}
                >
                  {subscription.cancel_at_period_end
                    ? "Active (Canceling)"
                    : subscription.status.charAt(0).toUpperCase() +
                      subscription.status.slice(1)}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Subscription ID
                </h3>
                <p className="font-mono text-gray-800">
                  {subscription.subscription_id}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Start Date
                </h3>
                <p className="text-gray-800">
                  {formatDate(subscription.start_date)}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  End Date
                </h3>
                <p className="text-gray-800">
                  {formatDate(subscription.end_date)}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Next Billing Date
                </h3>
                <p className="text-gray-800">
                  {formatDate(subscription.next_billing_date)}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Remaining Days
                </h3>
                <p className="text-gray-800">{subscription.remaining_days}</p>
              </div>

              {subscription.plan_duration_days && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Plan Duration
                  </h3>
                  <p className="text-gray-800">
                    {subscription.plan_duration_days} days
                  </p>
                </div>
              )}
            </div>

            {/* Cancellation Notice */}
            {subscription.status === "active" && !cancellationAllowed && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Cancellation Policy
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        You can cancel your subscription starting{" "}
                        {formatDate(cancellationDate?.toISOString())}(
                        {daysUntilCancellation} day
                        {daysUntilCancellation !== 1 ? "s" : ""} remaining).
                        This policy ensures you have adequate time to utilize
                        your subscription benefits.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-4 justify-center mt-6">
              {subscription.status === "active" && (
                <>
                  {subscription.cancel_at_period_end ? (
                    <button
                      disabled
                      className="py-2 px-6 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
                    >
                      Cancellation Scheduled for{" "}
                      {formatDate(subscription.next_billing_date)}
                    </button>
                  ) : cancellationAllowed ? (
                    <button
                      onClick={() => setShowCancelConfirmation(true)}
                      className="py-2 px-6 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      Cancel Subscription (No Refund)
                    </button>
                  ) : (
                    <button
                      disabled
                      className="py-2 px-6 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
                    >
                      Cancel Subscription (No Refund available in{" "}
                      {daysUntilCancellation} day
                      {daysUntilCancellation !== 1 ? "s" : ""})
                    </button>
                  )}
                </>
              )}

              {(subscription.status === "expired" ||
                subscription.status === "canceled" ||
                subscription.status === "grace_period") && (
                <button
                  onClick={() => setShowRenewModal(true)}
                  className="py-2 px-6 bg-[#4e7ab4] text-white rounded-lg hover:bg-[#3a5a8c] transition-colors"
                >
                  {subscription.status === "expired"
                    ? "Renew your Subscription"
                    : "Subscribe for Services"}
                </button>
              )}
            </div>
            {/* {cancellationAllowed ? (
                    <button
                      onClick={() => setShowCancelConfirmation(true)}
                      className="py-2 px-6 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Cancel Subscription
                    </button>
                  ) : (
                    <button
                      disabled
                      className="py-2 px-6 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
                      title={`Cancellation available from ${formatDate(
                        cancellationDate?.toISOString()
                      )}`}
                    >
                      Cancel Subscription (Available in {daysUntilCancellation}{" "}
                      day{daysUntilCancellation !== 1 ? "s" : ""})
                    </button>
                  )} */}
            {/* </>
              )}

              {(subscription.status === "expired" ||
                subscription.status === "canceled" ||
                subscription.status === "grace_period") && (
                <button
                  onClick={() => setShowRenewModal(true)}
                  className="py-2 px-6 bg-[#4e7ab4] text-white rounded-lg hover:bg-[#3a5a8c] transition-colors"
                >
                  {subscription.status === "expired"
                    ? "Renew your Subscription"
                    : "Subscribe for Services"}
                </button>
              )} */}
            {/* </div> */}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-6">
              You don't have an active subscription.
            </p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {plans.map((plan) => (
                <div
                  key={plan.plan_id}
                  className="border rounded-lg p-4 flex flex-col"
                >
                  <h3 className="text-lg font-semibold mb-2">{plan.name}</h3>
                  <p className="text-2xl font-bold mb-2">${plan.price}</p>
                  <p className="text-sm text-gray-600 mb-4">
                    {plan.billing_cycle}
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    {plan.description}
                  </p>
                  <button
                    onClick={() => handleRenewClick(plan)}
                    className="mt-auto py-2 px-4 bg-[#4e7ab4] text-white rounded-lg hover:bg-[#3a5a8c] transition-colors"
                  >
                    Select Plan
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4 text-red-600">
              Confirm Cancellation
            </h2>
            <p className="mb-4 text-gray-700">
              Your subscription will be canceled at the end of your current
              billing cycle. <br />
              <strong>No refund will be issued.</strong> <br />
              You will still have access to your subscription until{" "}
              <strong>
                {subscription.end_date
                  ? new Date(subscription.next_billing_date).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )
                  : "the next Billing Date"}
              </strong>{" "}
              and then a 7-day grace period.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowCancelConfirmation(false)}
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                Confirm Cancel (No Refund)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showRenewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Select a Plan</h3>

            {/* Plan selection in modal */}
            <div className="space-y-4 mb-6">
              {plans.map((plan) => (
                <div
                  key={plan.plan_id}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedPlan && selectedPlan.plan_id === plan.plan_id
                      ? "border-blue-500 bg-blue-50"
                      : "hover:border-gray-400"
                  }`}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold">{plan.name}</h4>
                      <p className="text-sm text-gray-600">
                        {plan.description}
                      </p>
                    </div>
                    <p className="font-bold">${plan.price}</p>
                  </div>
                </div>
              ))}
            </div>

            {selectedPlan && stripePromise ? (
              <Elements stripe={stripePromise}>
                <PaymentForm
                  plan={selectedPlan}
                  user_id={user_id}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setShowRenewModal(false)}
                />
              </Elements>
            ) : selectedPlan ? (
              <div className="text-center p-4">
                <p className="text-red-500 mb-4">
                  Stripe API key is missing. Please configure your environment.
                </p>
                <button
                  onClick={() => setShowRenewModal(false)}
                  className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex justify-end">
                <button
                  onClick={() => setShowRenewModal(false)}
                  className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDetails;
